/* eslint-disable react-hooks/exhaustive-deps */

import 'intersection-observer';
import { CustomAppContext, CustomPageComponent } from '../components/app/types';
import { useEffect } from 'react';
import App, { AppProps } from 'next/app';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import { UserInfo } from '@optimizely/react-sdk/dist/utils';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import cookies from 'nookies';
import { wrapper } from '../store';
import useAnalytics from '../shared/hooks/app/useAnalytics';
import { useApollo } from 'shared/graphql/apolloClient';
import { ApolloProvider } from '@apollo/client';
import {
    handleNhLsrpRouting,
    handleLsrpMapViewRouting,
    handleAuthPagesRouting
} from '../shared/helpers';
import {
    getOptimizelyUserInfo,
    OptimizelyDataFile,
    getOptimizelyOverrides,
    OptimizelyOverrides,
    useOptimizelyProviderProps,
    OptimizelyDevTogglesProps,
    OptimizelyOverridesContext
} from '@zoopla/optimizely';
import { setupLogger, SERVER_LOGGER, CLIENT_LOGGER } from '@zoopla/logger';

import { useFetchUser } from 'shared/hooks/useFetchUser';
import { ThemeProvider } from '@blueprint/ui';
import { zooplaTheme } from '@blueprint/theme';
import { CacheProvider } from '@emotion/react';
import { cache } from '@emotion/css';
import { NudgesTracker } from 'shared/authentication/tracker/tracker';
import { getWebChromeLayout } from 'shared/components/Layout/WebChrome';
import { getAnonId } from '@zoopla/tracking';

import { getDataFile as getOptimizelyDataFile } from '@zoopla/optimizely';

const OptimizelyDevToggles = dynamic<OptimizelyDevTogglesProps>(
    () => import('@zoopla/optimizely').then((mod) => mod.DevToggle),
    { ssr: false }
);

/**
 * These props are provided in _app.getInitialProps() atm.
 * _app.getInitialProps() will extend the pageProps object which contains the
 * initial pageProps that were preloaded for your page and its data fetching
 * methods. see [1]
 */
type ExtendedPageProps = {
    optimizelyUserInfo: UserInfo;
    optimizelyDatafile?: OptimizelyDataFile;
    initialApolloState: Record<string, unknown>;
    optimizelyOverrides?: OptimizelyOverrides;
};

interface CustomAppProps extends AppProps<ExtendedPageProps> {
    Component: AppProps<ExtendedPageProps>['Component'] & CustomPageComponent;
    pageProps: ExtendedPageProps;
}

const isDev = process.env.NODE_ENV === 'development';

function AppWrapper({ Component, pageProps }: CustomAppProps) {
    const getLayout = Component.getLayout || getWebChromeLayout;
    const { optimizelyDatafile, optimizelyOverrides, optimizelyProviderProps } =
        useOptimizelyProviderProps(pageProps);

    useAnalytics();
    useFetchUser();

    useEffect(() => {
        if (document) {
            document.documentElement.className = 'js';
        }
        NudgesTracker.setSession();
    }, []);
    const client = useApollo(pageProps.initialApolloState);

    useEffect(() => {
        if (!isDev) {
            Router.events.on('routeChangeStart', handleNhLsrpRouting);
            Router.events.on('routeChangeStart', handleLsrpMapViewRouting);
            Router.events.on('routeChangeStart', handleAuthPagesRouting);

            return () => {
                Router.events.off('routeChangeComplete', handleNhLsrpRouting);
                Router.events.off(
                    'routeChangeComplete',
                    handleLsrpMapViewRouting
                );
                Router.events.off(
                    'routeChangeComplete',
                    handleAuthPagesRouting
                );
            };
        }
    }, [Router.events]);

    return (
        <OptimizelyProvider {...optimizelyProviderProps}>
            <OptimizelyOverridesContext.Provider value={optimizelyOverrides}>
                <CacheProvider value={cache}>
                    <ThemeProvider theme={zooplaTheme}>
                        {isDev && (
                            <OptimizelyDevToggles
                                optimizelyDatafile={optimizelyDatafile}
                                optimizelyOverrides={optimizelyOverrides}
                            />
                        )}
                        <ApolloProvider client={client}>
                            {getLayout(<Component {...pageProps} />)}
                        </ApolloProvider>
                    </ThemeProvider>
                </CacheProvider>
            </OptimizelyOverridesContext.Provider>
        </OptimizelyProvider>
    );
}

/**
 * note that only the Page.getInitialProps() will return a populated pageProps
 * at ref [1]. Otherwise it will be an empty object due to the order of
 * execution as outlined below.
 *
 * SSR getServerSideProps:      _app.getInitialProps -> getServerSideProps -> combined for SSR render
 * Client getServerSideProps:   _app.getInitialProps -> getServerSideProps -> combined for Client render
 *
 * getStaticProps (at BUILD):   _app.getInitialProps (no request info) -> getStaticProps -> combined at build time for SSR render
 *
 * SSR Page.getInitialProps:    Page.getInitialProps -> _app.getInitialProps -> combined for SSR render
 * Client Page.getInitialProps: Page.getInitialProps -> _app.getInitialProps -> combined for Client render
 *
 * Required in order to make all pages non-statically optimised since we need
 * to fetch per-user data in _document.js (this could be refactored)
 */
AppWrapper.getInitialProps = async (appContext: CustomAppContext) => {
    const appProps = await App.getInitialProps(appContext);
    /** [1] */
    const pageProps: ExtendedPageProps = appProps.pageProps;
    const { ctx } = appContext;

    setupLogger(SERVER_LOGGER, CLIENT_LOGGER);

    const allCookies = cookies.get(ctx);

    const optimizelyOverrides = getOptimizelyOverrides(allCookies, ctx.query);

    const userAgent =
        typeof window === 'undefined'
            ? ctx.req?.headers['user-agent']
            : window.navigator.userAgent;

    const anonId = getAnonId(ctx);

    pageProps.optimizelyDatafile = await getOptimizelyDataFile();

    pageProps.optimizelyOverrides = optimizelyOverrides;
    pageProps.optimizelyUserInfo = getOptimizelyUserInfo(
        anonId,
        userAgent
    ) as UserInfo;

    return { ...appProps };
};

const appWithRedux = wrapper.withRedux(AppWrapper);

export default appWithRedux;
